import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Download,
  Description,
  GetApp,
  Visibility,
  CheckCircle,
  Label,
  Assignment,
  QrCode,
  LocalShipping,
  Inventory,
  Clear
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useActivityLog } from '../../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import FavoriteButton from '../../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../../constants/colors';

const PDQLabels = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [importFile, setImportFile] = useState(null);
  const [importFilePath, setImportFilePath] = useState('');
  const [labelType, setLabelType] = useState('shipping');
  const [labelSize, setLabelSize] = useState('4X6'); // shipping label size: 4X6 or 3X5
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processId, setProcessId] = useState(null);

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        '.csv',
        '.xlsx',
        '.xls'
      ];
      
      const isValidType = allowedTypes.some(type => 
        file.type === type || file.name.toLowerCase().endsWith(type)
      );
      
      if (isValidType) {
        setImportFile(file);
        setImportFilePath(file.name);
        setError('');
      } else {
        setError('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.');
      }
    }
  };

  // Fetch status for processing
  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current;
    if (!currentProcessId) return;

    try {
      const statusData = await apiCall(API_ENDPOINTS.PDQ_LABELS_STATUS(currentProcessId), {
        method: 'GET',
      });

      if (statusData) {
        const progressPct = statusData.progressPercentage ?? statusData.ProgressPercentage ?? 0;
        const totalItems = statusData.totalItems ?? statusData.TotalItems ?? 0;
        const processedItems = statusData.processedItems ?? statusData.ProcessedItems ?? 0;
        const logs = statusData.logs ?? statusData.Logs ?? [];
        const currentStatus = statusData.status ?? statusData.Status ?? '';

        updateActivityProcess(currentProcessId, {
          progressPercentage: progressPct,
          totalItems: totalItems,
          processedItems: processedItems,
        });

        if (logs && logs.length > 0) {
          setActivityLogs(currentProcessId, logs);
        }

        if (currentStatus === 'completed' || currentStatus === 'error') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          clearIntervalRef(currentProcessId);

          completeActivityProcess(currentProcessId, currentStatus === 'completed');

          if (currentStatus === 'completed') {
            setSuccess('Processing completed! Labels generated and downloaded successfully.');
            setResults({
              success: true,
              labelCount: processedItems,
              labelType: labelType
            });

            if (!downloadTriggeredRef.current) {
              downloadTriggeredRef.current = true;
              await downloadResult(currentProcessId);
            }
          } else {
            setError('Processing failed. Check the activity log for details.');
          }

          setProcessing(false);
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const downloadResult = async (downloadProcessId) => {
    try {
      const response = await apiCall(API_ENDPOINTS.PDQ_LABELS_DOWNLOAD(downloadProcessId));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const labelTypePrefix = labelType === 'shipping' ? 'Shipping' : 'Case';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `PDQ_${labelTypePrefix}_Labels_${timestamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleProcessLabels = async () => {
    if (!importFile) {
      setError('Please select an import file before processing.');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);
    downloadTriggeredRef.current = false;

    try {
      const formData = new FormData();
      formData.append('importFile', importFile);
      formData.append('labelType', labelType);
      // Only shipping labels have a size choice; case labels ignore it server-side.
      formData.append('labelSize', labelType === 'shipping' ? labelSize : '4X6');

      const result = await apiCall(API_ENDPOINTS.PDQ_LABELS_PROCESS, {
        method: 'POST',
        body: formData,
      });

      if (result.processId) {
        setProcessId(result.processId);
        processIdRef.current = result.processId;

        const labelTypeDisplay = labelType === 'shipping' ? 'Shipping' : 'Case';
        addActivityProcess(result.processId, `PDQ ${labelTypeDisplay} Label Generation`, 'PDQ Labels');

        intervalRef.current = setInterval(() => {
          fetchStatus();
        }, 1000);
        setIntervalRef(result.processId, intervalRef.current);
      } else {
        setError('Failed to start processing');
        setProcessing(false);
      }
    } catch (err) {
      setError(`Processing failed: ${err.message}`);
      setProcessing(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleDownloadShippingSample = async () => {
    try {
      const response = await apiCall(API_ENDPOINTS.PDQ_LABELS_SAMPLE_SHIPPING);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Shipping_Label_Sample_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const handleDownloadCaseSample = async () => {
    try {
      const response = await apiCall(API_ENDPOINTS.PDQ_LABELS_SAMPLE_CASE);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Case_Label_Sample_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };


  const resetForm = () => {
    setImportFile(null);
    setImportFilePath('');
    setError('');
    setSuccess('');
    setResults(null);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const droppedFile = droppedFiles[0];
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        '.csv',
        '.xlsx',
        '.xls'
      ];

      const isValidType = allowedTypes.some(type =>
        droppedFile.type === type || droppedFile.name.toLowerCase().endsWith(type)
      );

      if (isValidType) {
        setImportFile(droppedFile);
        setImportFilePath(droppedFile.name);
        setError('');
      } else {
        setError('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.');
      }
    }
  };

  const getLabelDescription = () => {
    if (labelType === 'shipping') {
      return 'Generate shipping labels with GTIN barcodes for pallet identification and tracking';
    } else {
      return 'Generate case marking labels with product information and GTIN barcodes';
    }
  };

  const getSampleColumns = () => {
    if (labelType === 'shipping') {
      return ['Department#', 'PO#', 'WMIT', 'Supplier Stock', 'GTIN', 'TO', 'Other'];
    } else {
      return ['Brand', 'Department Number', 'Supplier Stock No', 'Description', 'Pack / Units', 'Color', 'Size', 'GTIN'];
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="PDQ Full Pallet Pull Label" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          PDQ FULL PALLET PULL LABEL
        </Typography>

      <Grid container spacing={3}>
        {/* Main Processing Section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Label Generation
            </Typography>

            {/* Label Type Selection */}
            <Card sx={{ mb: 3, bgcolor: getCardBackground(theme) }}>
              <CardContent>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Label sx={{ color: TOOL_COLORS.PDF.primary }} />
                      Select Label Type
                    </Box>
                  </FormLabel>
                  <RadioGroup
                    value={labelType}
                    onChange={(e) => setLabelType(e.target.value)}
                    row
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value="shipping"
                      control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalShipping sx={{ color: TOOL_COLORS.PDF.primary }} />
                          <span>Shipping Label</span>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="case"
                      control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Inventory sx={{ color: TOOL_COLORS.PDF.primary }} />
                          <span>Case Label</span>
                        </Box>
                      }
                    />
                  </RadioGroup>

                  {/* Shipping labels can print at 4x6 or 3x5; case labels have one size */}
                  {labelType === 'shipping' && (
                    <Box sx={{ mt: 2 }}>
                      <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                        Label Size
                      </FormLabel>
                      <RadioGroup
                        value={labelSize}
                        onChange={(e) => setLabelSize(e.target.value)}
                        row
                      >
                        <FormControlLabel
                          value="4X6"
                          control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                          label={'4" × 6"'}
                        />
                        <FormControlLabel
                          value="3X5"
                          control={<Radio sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                          label={'3" × 5"'}
                        />
                      </RadioGroup>
                    </Box>
                  )}

                  <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                    {getLabelDescription()}
                  </Typography>
                </FormControl>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card sx={{ mb: 3, bgcolor: importFile ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Import File (Excel/CSV)
                  </Typography>
                  {importFile && (
                    <Tooltip title="File selected">
                      <CheckCircle sx={{ color: '#4CAF50' }} />
                    </Tooltip>
                  )}
                </Box>

                {/* Drag and Drop Zone */}
                <Box
                  component="label"
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    display: 'block',
                    border: `2px dashed ${isDragging ? TOOL_COLORS.PDF.primary : importFile ? '#4CAF50' : theme.palette.divider}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    bgcolor: isDragging
                      ? TOOL_COLORS.PDF.light
                      : importFile
                        ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                        : 'transparent',
                    transition: 'all 0.3s ease',
                    mb: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: importFile ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                      bgcolor: importFile
                        ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                        : TOOL_COLORS.PDF.light,
                    },
                  }}
                >
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                  />
                  {importFile ? (
                    <>
                      <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        1 file selected
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to replace
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: isDragging ? TOOL_COLORS.PDF.primary : 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {isDragging ? 'Drop file here' : 'Drag & drop file here or click to select'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Excel (.xlsx, .xls) or CSV files only
                      </Typography>
                    </>
                  )}
                </Box>
                {importFile && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      mb: 2,
                      bgcolor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                      <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {importFile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                        ({(importFile.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setImportFile(null)}
                      disabled={processing}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    onClick={handleDownloadShippingSample}
                    startIcon={<GetApp />}
                    sx={{ borderColor: theme.palette.divider }}
                  >
                    Shipping Sample
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleDownloadCaseSample}
                    startIcon={<GetApp />}
                    sx={{ borderColor: theme.palette.divider }}
                  >
                    Case Sample
                  </Button>

                  {importFile && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setImportFile(null);
                        setImportFilePath('');
                      }}
                      startIcon={<Clear />}
                      sx={{ borderColor: theme.palette.divider }}
                    >
                      Remove File
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Process Button */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleProcessLabels}
                disabled={!importFile || processing}
                sx={{ 
                  bgcolor: TOOL_COLORS.PDF.primary,
                  '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                  px: 4
                }}
              >
                {processing ? 'Generating Labels...' : 'Generate Labels'}
              </Button>
              
              {(importFile || results) && (
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  sx={{
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                    '&:hover': {
                      borderColor: theme.palette.text.secondary,
                      bgcolor: alpha(theme.palette.text.primary, 0.05)
                    }
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Status & Download Section */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Status & Downloads
            </Typography>

            {/* Progress */}
            {processing && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Generating {labelType === 'shipping' ? 'shipping' : 'case marking'} labels...
                </Typography>
                <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.PDF.primary } }} />
              </Box>
            )}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Success Alert */}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            {/* Results Summary */}
            {results && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Processing Results:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Label sx={{ color: TOOL_COLORS.PDF.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${results.labelCount} Labels Generated`}
                      secondary={`${labelType === 'shipping' ? 'Shipping labels' : 'Case marking labels'} with GTIN barcodes`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <QrCode sx={{ color: TOOL_COLORS.PDF.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="ITF-14 Barcodes"
                      secondary="GTIN barcodes generated for each label"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Assignment sx={{ color: TOOL_COLORS.PDF.primary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Label Size: ${labelType === 'shipping' ? '4" x 6"' : '6" x 4"'}`}
                      secondary="Optimized for thermal printers"
                    />
                  </ListItem>
                </List>
              </Box>
            )}

            {/* Download Section - File auto-downloads after generation */}
            {results && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Labels have been automatically downloaded. Check your downloads folder.
                </Alert>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Expected File Format */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Expected File Format:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
              {labelType === 'shipping' ? 'Shipping Label' : 'Case Label'} columns:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
              {getSampleColumns().map((column, index) => (
                <Chip
                  key={index}
                  label={column}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', borderColor: TOOL_COLORS.PDF.primary, color: theme.palette.text.primary }}
                />
              ))}
            </Box>

            {/* Instructions */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Instructions:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
              1. Select label type (Shipping or Case)<br />
              2. Download appropriate sample template<br />
              3. Upload populated Excel/CSV file<br />
              4. Generate labels with GTIN barcodes<br />
              5. Download PDF for thermal printing<br /><br />
              <strong>Barcode Format:</strong> ITF-14 barcodes generated from GTIN column
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default PDQLabels;